import { Bone, Object3D, Vector3 } from "three";

// TODO: completely rework this metadata
export enum PointType {
  FLOATING = "floating", // no data to drive it - just purely IK driven
  TARGETED = "targeted", // IK driven and tracking data driven - determined by weight
  KABSCH = "kabsch", // rigid point cloud used to drive orientation of untracked bone
  ROOT_BREAK = "root-break", // splits fabrik chain - must be targeted
}

export interface PointEntry {
  type: PointType | null;
  weight?: number;
  children?: string[];
  fabrikExtent?: [number, number];
}

export interface PointMetadata {
  [key: string]: PointEntry;
}

/**
 * Parsed by GeneralConstraint - a swing/twist limit constraint
 *
 * Used to specify GeeneralConstraint in JSON.
 */
export interface ConstraintMetadataEntry {
  parent: string;
  child: string;
  twistLims?: [number, number];
  xLims?: [number, number];
  zLims?: [number, number];
  relative?: boolean;
}

/**
 * Used in `EdgeStructure` to define `Joint` positions relative to three.js
 * `Bone` positions.
 */
export interface EdgePoint {
  /**
   * refs: a list of reference `Bone` names, if refs.length > 1, then we use the centroid
   */
  refs: string[];
  /**
   * offset vector in world space (allows us to say `Neck` `Bone` plus 0.1m in x)
   */
  offset?: [number, number, number];
  /**
   * offset vector where we specify two reference `Bone`s that define direction and
   * a length - allows us to move things along a line defined by the GLTF geometry
   * rather than guestimating or hand calculating
   */
  offsetRef?: {
    length: number;
    direction: [string, string];
  };
}

const bonePos = new Vector3();
const offset = new Vector3();

/**
 * Parses an `EdgePoint` into a `Vector3`
 *
 * @param out   output - point to write to
 * @param root  output - this is a part of the Object3D tree that contains all the
 *              referenceable `Bone`s - so the root `Bone` for example - we then naively
 *              query the tree by name (using `getObjectByName`)
 * @param point input - metadata point to parse
 * @returns     success
 */
export function Vector3FromEdgePoint(
  out: Vector3,
  root: Object3D,
  point: EdgePoint
): boolean {
  out.set(0, 0, 0);

  if (point.refs.length > 0) {
    for (const refName of point.refs) {
      const refBone = root.getObjectByName(refName);
      if (!refBone) {
        console.error(`unable to find ${refName} in skeleton`);
        return false;
      }

      refBone.getWorldPosition(bonePos);
      out.add(bonePos);
    }

    out.divideScalar(point.refs.length);
  }

  if (point.offset) {
    offset.fromArray(point.offset);
    out.add(offset);
  }

  if (point.offsetRef) {
    const offsetStart = root.getObjectByName(point.offsetRef.direction[0]);
    const offsetEnd = root.getObjectByName(point.offsetRef.direction[1]);

    if (!offsetStart || !offsetEnd) {
      console.error(
        `unable to find offsetRef ${point.offsetRef.direction} in skeleton`
      );
      return false;
    }

    offsetStart.getWorldPosition(bonePos);
    offsetEnd.getWorldPosition(offset);
    offset.sub(bonePos).multiplyScalar(point.offsetRef.length);

    out.add(offset);
  }

  return true;
}

export interface EdgeStructureEntry {
  parent?: string;

  start?: EdgePoint;
  end: EdgePoint;

  bones: string[];
  flags?: string[];
}

export interface EdgeStructure {
  [key: string]: EdgeStructureEntry;
}

export function dummyPointData(names: string[]) {
  const entry = { type: PointType.TARGETED };
  const pointData = {} as PointMetadata;

  for (const name of names) {
    pointData[name] = entry;
  }

  return pointData;
}
